import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './common/Header';
import Footer from './common/Footer';
import { getValidSession } from './helpers/sessionUtils';
import { useNavigate } from 'react-router-dom';
import './SuggestedVendors.css';

import { formatDateOnly } from '../utils/dateUtils';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return formatDateOnly(dateStr);
};

const categoryLabel = (cat) => {
    if (!cat) return '—';
    return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

function SuggestedVendors() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });

    useEffect(() => {
        const session = getValidSession();
        if (!session?.user) {
            navigate('/login');
            return;
        }
        fetchData(1, session.user.token);
        // eslint-disable-next-line
    }, []);

    const fetchData = async (page = 1, token) => {
        try {
            setLoading(true);
            setError(null);
            const authToken = token || localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/recommendations/customer/suggested-vendors`, {
                params: { page, limit: 10 },
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.data.success) {
                setSessions(response.data.data);
                setPagination({
                    page: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    totalItems: response.data.totalItems
                });
            }
        } catch (err) {
            setError('Failed to fetch suggested vendors. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <div className="sv-wrapper">
                <div className="sv-hero">
                    <div className="container">
                        <h1 className="sv-hero-title">Suggested Vendors</h1>
                        <p className="sv-hero-sub">
                            Vendors we recommended to you based on your past enquiries
                        </p>
                    </div>
                </div>

                <div className="container sv-content">
                    {loading ? (
                        <div className="sv-loading">
                            <div className="sv-spinner"></div>
                            <p>Loading your vendor recommendations...</p>
                        </div>
                    ) : error ? (
                        <div className="sv-empty sv-error">
                            <i className="bi bi-exclamation-triangle-fill sv-empty-icon"></i>
                            <h3>Something went wrong</h3>
                            <p>{error}</p>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="sv-empty">
                            <i className="bi bi-inbox sv-empty-icon"></i>
                            <h3>No Recommendations Yet</h3>
                            <p>When you enquire about a service, we'll send you vendor recommendations here.</p>
                            <a href="/" className="sv-explore-btn">Explore Services</a>
                        </div>
                    ) : (
                        <>
                            {sessions.map((session) => (
                                <div className="sv-session-card" key={session.id}>
                                    <div className="sv-session-header">
                                        <div>
                                            <span className="sv-session-category">{categoryLabel(session.category)}</span>
                                            <span className="sv-session-date">{formatDate(session.created_at)}</span>
                                        </div>
                                        <span className={`sv-session-badge ${session.status === 'completed' ? 'sv-badge-done' : 'sv-badge-active'}`}>
                                            {session.status === 'completed' ? 'Completed' : 'Active'}
                                        </span>
                                    </div>

                                    {session.history && session.history.length > 0 ? (
                                        <div className="sv-vendor-grid">
                                            {session.history.map((h) => (
                                                <div className="sv-vendor-card" key={h.id}>
                                                    <div className="sv-vendor-avatar">
                                                        {(h.vendor?.name || h.vendor?.company_name || 'V').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="sv-vendor-info">
                                                        <h4 className="sv-vendor-name">
                                                            {h.vendor?.company_name || h.vendor?.name || 'Vendor'}
                                                        </h4>
                                                        {h.vendor?.city && (
                                                            <p className="sv-vendor-city">
                                                                <i className="bi bi-geo-alt-fill"></i>
                                                                {h.vendor.city}
                                                            </p>
                                                        )}
                                                        {h.vendor?.phone && (
                                                            <a href={`tel:${h.vendor.phone}`} className="sv-vendor-phone">
                                                                <i className="bi bi-telephone-fill"></i>
                                                                {h.vendor.phone}
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="sv-vendor-batch">Batch {h.batch_no}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="sv-no-vendors">No vendors recorded for this session.</p>
                                    )}
                                </div>
                            ))}

                            {pagination.totalPages > 1 && (
                                <div className="sv-pagination">
                                    <button
                                        className="sv-page-btn"
                                        disabled={pagination.page <= 1}
                                        onClick={() => fetchData(pagination.page - 1)}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                        <button
                                            key={p}
                                            className={`sv-page-btn ${p === pagination.page ? 'sv-page-active' : ''}`}
                                            onClick={() => fetchData(p)}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        className="sv-page-btn"
                                        disabled={pagination.page >= pagination.totalPages}
                                        onClick={() => fetchData(pagination.page + 1)}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default SuggestedVendors;
